import { backendApi } from "@/services/backend/api";
import {
  CommitmentList,
  CommitmentStatus,
  List,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import { UUID } from "@bitory-gmbh/nidavellir";
import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { ServiceKind } from "@bitory-gmbh/bsm";

const relationshipApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    relationshipsRelationshipCreate: build.mutation<
      Relationship,
      CreateRequest
    >({
      query: (request) => ({
        url: `relationships/relationship`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationship", id: "LIST" },
      ],
    }),
    relationshipsRelationshipShow: build.query<Relationship, ShowRequest>({
      query: (request) => ({
        url: `relationships/relationship`,
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
      ],
    }),
    relationshipsRelationshipList: build.query<List, ListRequest>({
      query: (request) => ({
        url: `relationships/relationship/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        providesList(result?.relationships, "RelationshipsRelationship"),
    }),
    relationshipsRelationshipListCommitments: build.query<
      CommitmentList,
      ListCommitmentsRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/list/commitments`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        providesList(
          result?.commitments,
          "RelationshipsRelationshipCommitment",
        ),
    }),
    relationshipsRelationshipAddCommitment: build.mutation<
      Relationship,
      AddCommitmentRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipCreateAndAssignDocument: build.mutation<
      Relationship,
      CreateAndAssignDocumentRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment/document`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
      ],
    }),
    relationshipsRelationshipUpdateCommitmentStatus: build.mutation<
      Relationship,
      UpdateCommitmentStatusRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment/status`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipUpdateCommitmentActualHours: build.mutation<
      Relationship,
      UpdateCommitmentActualHoursRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment/actual-hours`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipUpdateCommitmentComment: build.mutation<
      Relationship,
      UpdateCommitmentCommentRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment/comment`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipCreateSeries: build.mutation<
      Relationship,
      CreateSeriesRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment/series`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipRePopulateSeries: build.mutation<
      Relationship,
      RePopulateSeriesRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment/re-populate-series`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipCopyToFutureCommitments: build.mutation<
      Relationship,
      CopyToFutureCommitmentsRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment/copy-to-future`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationship", id: "LIST" },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
      ],
    }),
    relationshipsRelationshipDeleteCommitment: build.mutation<
      Relationship,
      DeleteCommitmentRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipDeleteCommitments: build.mutation<
      Relationship,
      DeleteCommitmentsRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitments`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: () => [
        "RelationshipsRelationship",
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipDeleteSeries: build.mutation<
      Relationship,
      DeleteSeriesRequest
    >({
      query: (request) => ({
        url: `relationships/relationship/commitment/series`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
    relationshipsRelationshipDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `relationships/relationship`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsRelationship", id: request.id },
        { type: "RelationshipsRelationship", id: "LIST" },
        { type: "RelationshipsRelationshipCommitment", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useRelationshipsRelationshipCreateMutation,
  useRelationshipsRelationshipShowQuery,
  useRelationshipsRelationshipListQuery,
  useRelationshipsRelationshipListCommitmentsQuery,
  useRelationshipsRelationshipAddCommitmentMutation,
  useRelationshipsRelationshipCreateSeriesMutation,
  useRelationshipsRelationshipRePopulateSeriesMutation,
  useRelationshipsRelationshipCreateAndAssignDocumentMutation,
  useRelationshipsRelationshipUpdateCommitmentStatusMutation,
  useRelationshipsRelationshipUpdateCommitmentActualHoursMutation,
  useRelationshipsRelationshipUpdateCommitmentCommentMutation,
  useRelationshipsRelationshipCopyToFutureCommitmentsMutation,
  useRelationshipsRelationshipDeleteCommitmentMutation,
  useRelationshipsRelationshipDeleteCommitmentsMutation,
  useRelationshipsRelationshipDeleteSeriesMutation,
  useRelationshipsRelationshipDeleteMutation,
} = relationshipApi;

export interface CreateRequest {
  id: UUID;
  companyId: UUID;
  branchId: UUID;
  customerId: UUID;
  serviceObjectId: UUID;
  start: string;
  end: string;
}

export interface ShowRequest {
  id: UUID;
}

export interface ListRequest {
  company?: Filter<UUID>;
  branch?: Filter<UUID>;
  customer?: Filter<UUID>;
  serviceObject?: Filter<UUID>;
  process?: Filter<UUID>;
  serviceKind?: Filter<ServiceKind>;
  nextCommitmentAt?: Filter<UUID>;
  start?: Filter<string>;
  end?: Filter<string>;
  orderBy?: OrdrBy<string>[];
  limit?: number;
  offset?: number;
}

export interface ListCommitmentsRequest {
  relationship?: Filter<UUID>;
  series?: Filter<UUID>;
  company?: Filter<UUID>;
  branch?: Filter<UUID>;
  customer?: Filter<UUID>;
  process?: Filter<UUID>;
  serviceObject?: Filter<UUID>;
  serviceKind?: Filter<ServiceKind>;
  status?: Filter<CommitmentStatus>;
  date?: Filter<string>;
  orderBy?: OrdrBy<ListCommitmentsOrderBy>[];
  limit?: number;
  offset?: number;
}

export type ListCommitmentsOrderBy =
  | "serviceKind"
  | "status"
  | "customer"
  | "cycle"
  | "date"
  | "createdAt"
  | "updatedAt";

export interface AddCommitmentRequest {
  id: UUID;
  commitmentId: UUID;
  serviceKind: ServiceKind;
  date: string;
  cycle: number;
}

export interface CreateAndAssignDocumentRequest {
  id: UUID;
  commitmentId: UUID;
  serviceKind: ServiceKind;
}

export interface UpdateCommitmentStatusRequest {
  id: UUID;
  commitmentId: UUID;
  status: CommitmentStatus;
}

export interface UpdateCommitmentActualHoursRequest {
  id: UUID;
  commitmentId: UUID;
  actualHours: number;
}

export interface UpdateCommitmentCommentRequest {
  id: UUID;
  commitmentId: UUID;
  comment: string;
}

export interface CreateSeriesRequest {
  id: UUID;
  commitmentId: UUID;
  firstDate: string;
}

export interface RePopulateSeriesRequest {
  id: UUID;
  commitmentId: UUID;
}

export interface CopyToFutureCommitmentsRequest {
  id: UUID;
  commitmentId: UUID;
}

export interface DeleteCommitmentRequest {
  id: UUID;
  commitmentId: UUID;
}

export interface DeleteCommitmentsRequest {
  commitments: { relationshipId: UUID; commitmentId: UUID }[];
}

export interface DeleteSeriesRequest {
  id: UUID;
  seriesId: UUID;
}

export interface DeleteRequest {
  id: UUID;
}
